import { useOutlookAdaptor } from '@/contexts/useOutlookAdaptor';
import { getRestfulId } from '@/contexts/utils/getRestfulId';
import {
  useCreateOutlookReplyAllMutation,
  useSendOutlookMessageMutation,
} from '@/generated/graphql';
import { useToast } from '@shared/components/toast';
import { useAnalytics } from '@shared/hooks/useAnalytics';

type UseReplyAllState = {
  loadingDraftReplyAll: boolean;
};

type UseReplyAllActions = {
  draftReplyAll: (itemId: string, content?: string) => Promise<void>;
};

export const useReplyAll = (): UseReplyAllState & UseReplyAllActions => {
  const { item, sharedMailboxAddress, viewedMailboxAddress } =
    useOutlookAdaptor();
  const { sendToast } = useToast();
  const [draftReplyAllMutation, { loading: loadingDraftReplyAll }] =
    useCreateOutlookReplyAllMutation();
  const [sendMessageMutation] = useSendOutlookMessageMutation();
  const { track } = useAnalytics();

  const sendMessage = async (messageId: string) => {
    const isSent = await sendMessageMutation({
      variables: {
        input: {
          messageId,
          sharedInbox: sharedMailboxAddress,
          mailboxAddress: viewedMailboxAddress,
        },
      },
    })
      .then((res) => Boolean(res.data?.sendOutlookMessage?.success))
      .catch((error: unknown) => {
        console.error('Failed to send Outlook message', error);
        return false;
      });

    if (!isSent) {
      track('Failed to Send Reply All');
      sendToast('Failed to send message', {
        description: 'Something went wrong sending your message.',
        variant: 'error',
      });
    } else {
      track('Sent Reply All');
      sendToast('Success!', {
        description: 'Your message was sent.',
        variant: 'success',
      });
    }
  };

  const draftReplyAll = async (itemId: string, content?: string) => {
    const restfulItemId = item ? getRestfulId(item) : '';
    const internetMessageId = item?.internetMessageId ?? '';
    const isItemInFocus = itemId === restfulItemId;
    const messageId = Office.context.mailbox.convertToRestId(
      itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );

    track('Attempted Draft Reply All', {
      data: {
        itemId,
        restfulItemId,
        internetMessageId,
        isItemInFocus,
        messageId,
        content,
        sharedInbox: sharedMailboxAddress,
      },
    });

    const res = await draftReplyAllMutation({
      variables: {
        input: {
          messageId,
          // NOTE(parlato): If the item being replied to is not in focus,
          // we should not use focused item's internet message id. However, I
          // believe this "fix" is causing more harm than good - users are having
          // trouble insert replies even to the correct, focused message.
          internetMessageId,
          content,
          sharedInbox: sharedMailboxAddress,
          mailboxAddress: viewedMailboxAddress,
        },
      },
    });

    const draftMessageId = res?.data?.createOutlookReplyAll?.messageId;
    if (!res.data?.createOutlookReplyAll?.success) {
      sendToast('Failed to compose message', {
        description: 'Something went wrong drafting your message.',
        variant: 'error',
      });
    } else if (draftMessageId && isItemInFocus) {
      sendToast('Success!', {
        description: 'Your message was drafted.',
        variant: 'success',
        autoHideDuration: 7 * 1000,
        actions: [
          {
            label: 'Send message',
            onPress: () => {
              void sendMessage(draftMessageId);
            },
          },
        ],
      });
    } else if (draftMessageId) {
      Office.context.mailbox.displayMessageForm(draftMessageId);
    }
  };

  return { loadingDraftReplyAll, draftReplyAll };
};
